<template>
  <div class="min-h-screen bg-white">
    <!-- Hero Section -->
    <section class="bg-gradient-to-br from-blue-50 to-indigo-100 py-20">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center">
          <h1 class="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
            Contact
          </h1>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">
            Une question ? Un projet ? N'hésitez pas à nous contacter pour découvrir comment Anamnesis peut vous accompagner
          </p>
        </div>
      </div>
    </section>

    <!-- Contact Section -->
    <section class="py-20 bg-white">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <!-- Formulaire de contact -->
          <div>
            <h2 class="text-3xl font-bold text-gray-900 mb-8">Envoyez-nous un message</h2>
            <form class="space-y-6">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label for="firstName" class="block text-sm font-medium text-gray-700 mb-2">
                    Prénom *
                  </label>
                  <input
                    id="firstName"
                    type="text"
                    name="firstName"
                    required
                    class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-300"
                    placeholder="Votre prénom"
                  />
                </div>
                <div>
                  <label for="lastName" class="block text-sm font-medium text-gray-700 mb-2">
                    Nom *
                  </label>
                  <input
                    id="lastName"
                    type="text"
                    name="lastName"
                    required
                    class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-300"
                    placeholder="Votre nom"
                  />
                </div>
              </div>

              <div>
                <label for="email" class="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  id="email"
                  type="email"
                  name="email"
                  required
                  class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-300"
                  placeholder="votre.email@exemple.com"
                />
              </div>

              <div>
                <label for="organization" class="block text-sm font-medium text-gray-700 mb-2">
                  Organisation
                </label>
                <input
                  id="organization"
                  type="text"
                  name="organization"
                  class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-300"
                  placeholder="Votre institution ou organisation"
                />
              </div>

              <div>
                <label for="subject" class="block text-sm font-medium text-gray-700 mb-2">
                  Sujet *
                </label>
                <select
                  id="subject"
                  name="subject"
                  required
                  class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-300"
                >
                  <option value="">Sélectionnez un sujet</option>
                  <option value="demo">Demande de démonstration</option>
                  <option value="collaboration">Proposition de collaboration</option>
                  <option value="support">Support technique</option>
                  <option value="formation">Formation</option>
                  <option value="partenariat">Partenariat</option>
                  <option value="autre">Autre</option>
                </select>
              </div>

              <div>
                <label for="message" class="block text-sm font-medium text-gray-700 mb-2">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows="6"
                  required
                  class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-300 resize-none"
                  placeholder="Décrivez votre projet ou votre demande..."
                ></textarea>
              </div>

              <div class="flex items-start">
                <input
                  id="consent"
                  type="checkbox"
                  name="consent"
                  required
                  class="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label for="consent" class="ml-3 text-sm text-gray-600">
                  J'accepte que mes données soient utilisées pour répondre à ma demande de contact *
                </label>
              </div>

              <button
                type="submit"
                class="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-300 transform hover:scale-105"
              >
                Envoyer le message
              </button>
            </form>
          </div>

          <!-- Informations de contact -->
          <div>
            <h2 class="text-3xl font-bold text-gray-900 mb-8">Informations de contact</h2>
            
            <!-- Laboratoire MAP -->
            <div class="bg-gray-50 rounded-xl p-6 mb-8">
              <h3 class="text-xl font-semibold text-gray-900 mb-4">Laboratoire MAP</h3>
              <div class="space-y-4">
                <div class="flex items-start">
                  <MapPinIcon class="w-5 h-5 text-blue-600 mr-3 mt-1" />
                  <div>
                    <p class="text-gray-700 font-medium">Adresse</p>
                    <p class="text-gray-600">
                      184 avenue de Luminy<br />
                      13288 Marseille Cedex 09<br />
                      France
                    </p>
                  </div>
                </div>
                
                <div class="flex items-center">
                  <PhoneIcon class="w-5 h-5 text-blue-600 mr-3" />
                  <div>
                    <p class="text-gray-700 font-medium">Téléphone</p>
                    <p class="text-gray-600">+33 (0)4 91 82 85 25</p>
                  </div>
                </div>
                
                <div class="flex items-center">
                  <MailIcon class="w-5 h-5 text-blue-600 mr-3" />
                  <div>
                    <p class="text-gray-700 font-medium">Email</p>
                    <p class="text-gray-600">anamnesis@map.cnrs.fr</p>
                  </div>
                </div>
                
                <div class="flex items-center">
                  <GlobeIcon class="w-5 h-5 text-blue-600 mr-3" />
                  <div>
                    <p class="text-gray-700 font-medium">Site web</p>
                    <a href="https://map.cnrs.fr" class="text-blue-600 hover:text-blue-700">
                      map.cnrs.fr
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <!-- Horaires -->
            <div class="bg-blue-50 rounded-xl p-6 mb-8">
              <h3 class="text-xl font-semibold text-gray-900 mb-4">Horaires d'ouverture</h3>
              <div class="space-y-2">
                <div class="flex justify-between">
                  <span class="text-gray-700">Lundi - Vendredi</span>
                  <span class="text-gray-600">9h00 - 17h30</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-700">Samedi - Dimanche</span>
                  <span class="text-gray-600">Fermé</span>
                </div>
              </div>
              <p class="text-sm text-gray-600 mt-4">
                * Support technique disponible du lundi au vendredi
              </p>
            </div>

            <!-- Réseaux sociaux -->
            <div class="bg-green-50 rounded-xl p-6">
              <h3 class="text-xl font-semibold text-gray-900 mb-4">Suivez-nous</h3>
              <div class="flex space-x-4">
                <a href="#" class="bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-lg transition-colors duration-300">
                  <TwitterIcon class="w-5 h-5" />
                </a>
                <a href="#" class="bg-blue-800 hover:bg-blue-900 text-white p-3 rounded-lg transition-colors duration-300">
                  <LinkedinIcon class="w-5 h-5" />
                </a>
                <a href="#" class="bg-gray-800 hover:bg-gray-900 text-white p-3 rounded-lg transition-colors duration-300">
                  <GithubIcon class="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ Section -->
    <section class="py-20 bg-gray-50">
      <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-16">
          <h2 class="text-3xl font-bold text-gray-900 mb-4">
            Questions fréquentes
          </h2>
          <p class="text-lg text-gray-600">
            Trouvez rapidement les réponses aux questions les plus courantes
          </p>
        </div>

        <div class="space-y-6">
          <div class="bg-white rounded-xl p-6 shadow-sm">
            <h3 class="text-lg font-semibold text-gray-900 mb-3">
              Comment puis-je accéder à Anamnesis ?
            </h3>
            <p class="text-gray-600">
              Anamnesis est accessible via votre navigateur web. Contactez-nous pour obtenir vos identifiants d'accès ou demander une démonstration.
            </p>
          </div>

          <div class="bg-white rounded-xl p-6 shadow-sm">
            <h3 class="text-lg font-semibold text-gray-900 mb-3">
              Anamnesis est-il gratuit ?
            </h3>
            <p class="text-gray-600">
              Anamnesis est développé dans le cadre de projets de recherche publique. Les modalités d'accès dépendent de votre statut et de votre projet. Contactez-nous pour plus d'informations.
            </p>
          </div>

          <div class="bg-white rounded-xl p-6 shadow-sm">
            <h3 class="text-lg font-semibold text-gray-900 mb-3">
              Proposez-vous des formations ?
            </h3>
            <p class="text-gray-600">
              Oui, nous organisons régulièrement des sessions de formation pour les nouveaux utilisateurs. Ces formations peuvent être adaptées aux besoins spécifiques de votre institution.
            </p>
          </div>

          <div class="bg-white rounded-xl p-6 shadow-sm">
            <h3 class="text-lg font-semibold text-gray-900 mb-3">
              Mes données sont-elles sécurisées ?
            </h3>
            <p class="text-gray-600">
              La sécurité des données est notre priorité. Anamnesis respecte les standards de sécurité les plus élevés et les réglementations en vigueur (RGPD).
            </p>
          </div>
        </div>
      </div>
    </section>
  </div>
  <AppFooter />
</template>

<script lang="ts">
import AppFooter from '@/components/UI/AppFooter.vue';
import {
    GithubIcon,
    GlobeIcon,
    LinkedinIcon,
    MailIcon,
    MapPinIcon,
    PhoneIcon,
    TwitterIcon
} from "lucide-vue-next";
import { defineComponent } from "vue";

export default defineComponent({
  name: "Contact",
  components: {
    MapPinIcon,
    PhoneIcon,
    MailIcon,
    GlobeIcon,
    TwitterIcon,
    LinkedinIcon,
    GithubIcon,
    AppFooter
  }
});
</script>

<style scoped>
.focus\:ring-2:focus {
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.5);
}

.focus\:border-transparent:focus {
  border-color: transparent;
}

.hover\:scale-105:hover {
  transform: scale(1.05);
}
</style> 